import {
  Component,
  inject,
  OnInit,
  signal,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import { ActivatedRoute, Data, Params } from '@angular/router';
import { InlineInfoComponent } from '@catch/general-ui';
import { MarkdownViewerComponent } from '@catch/markdown-viewer';
import { TranslocoPipe } from '@jsverse/transloco';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { AsyncPipe } from '@angular/common';
import { HeaderTagsComponent } from '../../snippets/header-tags/header-tags.component';
import { JobApplyActionsComponent } from '../../snippets/job-apply-actions/job-apply-actions.component';
import { AnimatedCheckComponent } from '../../snippets/animated-check/animated-check.component';
import { AnimatedCrossComponent } from '../../snippets/animated-cross/animated-cross.component';
import { Observable, zip } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CompanyInfo } from '../../../interfaces/company-info';
import { JobDetail } from '../../../interfaces/job-detail';
import { ColorService } from '../../../services/color.service';
import { EmbedService } from '../../../services/embed.service';
import { FontawesomeService } from '../../../services/fontawesome.service';
import { GoogleJobPostingService } from '../../../services/google-job-posting.service';
import { RefService } from '../../../services/ref.service';
import { SEOService } from '../../../services/seo.service';
import { PlatformAuthModalPanel } from '../../../platform/enums/platform-auth-modal-panel.enum';
import { PlatformAuthService } from '../../../platform/services/platform-auth.service';
import { PlatformJobboardLoginModalService } from '../../../platform/services/platform-jobboard-login-modal.service';

@UntilDestroy()
@Component({
  selector: 'app-job-recommended',
  templateUrl: './job-recommended.component.html',
  styleUrls: ['./job-recommended.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    AsyncPipe,
    TranslocoPipe,
    InlineInfoComponent,
    MarkdownViewerComponent,
    HeaderTagsComponent,
    AnimatedCheckComponent,
    AnimatedCrossComponent,
    FaIconComponent,
    JobApplyActionsComponent,
  ],
})
export class JobRecommendedComponent implements OnInit {
  public readonly colorService = inject(ColorService);
  private readonly googleJobsService = inject(GoogleJobPostingService);
  public readonly refService = inject(RefService);
  private readonly route = inject(ActivatedRoute);
  private readonly seoService = inject(SEOService);
  public readonly auth = inject(PlatformAuthService);
  private readonly authModal = inject(PlatformJobboardLoginModalService);
  public readonly embedService = inject(EmbedService);
  public readonly fontawesomeService = inject(FontawesomeService);

  job$: Observable<JobDetail>;
  companyInfo$: Observable<CompanyInfo>;
  linkValid: WritableSignal<boolean> = signal(false);
  jobId: number;
  nonce: string;

  submitSuccess: WritableSignal<boolean> = signal(false);

  ngOnInit(): void {
    this.job$ = this.route.data.pipe(map((data: Data) => data['job']));
    this.companyInfo$ = this.route.data.pipe(
      map((data: Data) => data['companyInfo'])
    );
    this.companyInfo$.pipe(untilDestroyed(this)).subscribe({
      next: (company: CompanyInfo) => {
        this.seoService.updateImage(company.logoURL);
      },
    });
    zip(this.job$, this.companyInfo$)
      .pipe(untilDestroyed(this))
      .subscribe(([job, companyInfo]) => {
        this.googleJobsService.setStructuredData(job, companyInfo);
      });
    const nonce = this.route.snapshot.queryParams['nonce'];
    this.job$
      .pipe(
        tap((job: JobDetail) => {
          this.jobId = job.id;
          this.colorService.setDesign(job.companyID);
          this.seoService.setWebsite(job.website);
          this.refService.countJobClick(job.companyID, job.id);
          this.nonce = nonce;
          this.linkValid.set(true);
        }),
        switchMap((job: JobDetail) => this.seoService.updateJobPageSeo(job)),
        untilDestroyed(this)
      )
      .subscribe();
  }

  /**
   * Opens the candidate registration modal on the jobboard.
   */
  openRegister(): void {
    this.authModal.open({ panel: PlatformAuthModalPanel.REGISTER });
  }

  /**
   * Builds the public apply URL for a recommended job.
   * @param job - Public job detail
   * @returns Router commands
   */
  applyLink(job: JobDetail): (string | number)[] {
    return ['/companies', job.companyID, 'jobs', job.id, 'apply'];
  }

  /**
   * Query params that keep the recommendation nonce on the apply form.
   * @returns Query params or null
   */
  applyQueryParams(): Params | null {
    return this.nonce ? { recommendationNonce: this.nonce } : null;
  }
}
